import React from "react";
import { useNavigate } from "react-router-dom";
import { PenLine, Eye, RefreshCw, Globe, BadgeCheck, Check, Lock, ClipboardCheck } from "lucide-react";

const STAGES = [["brief", "Brief", ClipboardCheck], ["draft", "Draft", PenLine], ["review", "Review", Eye], ["rework", "Rework", RefreshCw], ["publish", "Publish", Globe], ["verify", "Verify", BadgeCheck]];
const ORDER = { brief: -1, draft: 0, review: 1, rework: 2, approved: 3, publish: 3, verify: 4, done: 5 };

export function FlowStages({ stage, round = 1, publishEnabled = true, verifyEnabled = true, testId = "cf-stages" }) {
  const cur = ORDER[stage] ?? 0;
  const list = STAGES.filter(([k]) => (k !== "publish" || publishEnabled) && (k !== "verify" || verifyEnabled));
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testId}>
      {list.map(([k, label, I]) => {
        const idx = ORDER[k];
        const done = stage === "done" || idx < cur;
        const active = stage === k;
        return (
          <span key={k} data-testid={`${testId}-${k}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border ${active ? "bg-[#06060C] text-white border-[#06060C]" : done ? "bg-[#E6F7EE] text-[#1f9d55] border-[#1f9d55]/20" : "bg-white text-[#9FA1A4] border-[#EEEFEF]"}`}>
            {done && !active ? <Check className="h-3 w-3" /> : <I className="h-3 w-3" />}{label}{k === "rework" && round > 1 ? ` ·${round - 1}` : ""}
          </span>
        );
      })}
      {stage === "done" && <span className="text-[10px] font-mono-data uppercase tracking-wider text-[#1f9d55] ml-1">Complete</span>}
    </div>
  );
}

export function FlowTaskBanner({ t, onOpenDoc }) {
  const cf = t.content_flow;
  return (
    <div className="rounded-2xl border border-[#06060C]/10 bg-[#F7F4FF] p-3.5 space-y-2" data-testid="cf-task-banner">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#5b2fd6]">Content flow · {cf.flow_name}</span>
        {t.flow_locked && <span className="text-[11px] text-[#595D62] flex items-center gap-1" data-testid="cf-task-locked"><Lock className="h-3 w-3" /> Status locked in Active Flow until the doc is approved</span>}
        {cf.doc_id && <button data-testid="cf-task-open-doc" onClick={() => onOpenDoc(cf.doc_id)} className="ml-auto rounded-full bg-[#06060C] text-white text-xs px-3 py-1 hover:bg-[#1c1c24]">Open doc</button>}
      </div>
      <FlowStages stage={cf.stage} round={cf.round} publishEnabled={cf.config?.publish?.enabled} verifyEnabled={cf.config?.verify?.enabled} testId="cf-task-stages" />
    </div>
  );
}

const STEP_LABEL = { brief: "Brief & strategy", draft: "First draft", review: "Review", rework: "Rework", publish: "Publish", verify: "Verify" };

export function TaskFlowSection({ t, onLeave }) {
  const nav = useNavigate();
  const open = (id) => { onLeave?.(); setTimeout(() => nav(`/console/docs/${id}`), 0); };
  if (t.content_flow) return <FlowTaskBanner t={t} onOpenDoc={open} />;
  if (!t.flow_step || !t.doc_id) return null;
  return (
    <div className="rounded-2xl border border-[#EEEFEF] bg-[#F7F4FF] px-3.5 py-2.5 flex items-center gap-2" data-testid="cf-step-banner">
      <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#5b2fd6]">Content flow step · {STEP_LABEL[t.flow_step]}</span>
      <span className="text-[11px] text-[#9FA1A4]">{t.flow_step === "brief" ? "Read the brief and strategy, then mark this subtask Completed to start the first draft." : "Work happens in the doc. Its buttons move the flow on."}</span>
      <button data-testid="cf-step-open-doc" onClick={() => open(t.doc_id)} className="ml-auto rounded-full bg-[#06060C] text-white text-xs px-3 py-1 hover:bg-[#1c1c24]">Open doc</button>
    </div>
  );
}

const BADGE_ICON = { brief: ClipboardCheck, draft: PenLine, review: Eye, rework: RefreshCw };

const SOCIAL_STEP = { brief: "Brief & strategy", draft: "Draft posts", review: "Review posts", rework: "Revise posts", schedule: "Schedule posts" };
const SOCIAL_STATE = {
  brief: "Brief and strategy are being reviewed", draft: "Draft needs to be completed first", review: "Locked: in internal review", rework: "Posts are being revised", client: "Locked: with the client for approval",
  schedule: "Approved: ready to schedule", done: "Posts scheduled",
};

export function SocialFlowSection({ t, onLeave }) {
  const nav = useNavigate();
  const gid = t.social_group_id;
  const open = () => { onLeave?.(); setTimeout(() => nav(`/console/social/${gid}`), 0); };
  const sf = t.social_flow;
  const locked = sf && ["review", "client"].includes(sf.stage);
  return (
    <div className="rounded-2xl border border-[#06060C]/10 bg-[#F7F4FF] px-3.5 py-3 flex items-center gap-2 flex-wrap" data-testid={sf ? "sf-task-banner" : "sf-step-banner"}>
      <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#5b2fd6]">{sf ? `Social flow · ${sf.flow_name}` : SOCIAL_STEP[t.social_step]}</span>
      <span className="text-[11px] text-[#595D62] flex items-center gap-1" data-testid="sf-task-state">{locked && <Lock className="h-3 w-3" />}{sf ? SOCIAL_STATE[sf.stage] : t.social_step === "brief" ? "Read the brief and strategy, then mark this subtask Completed to start drafting posts." : "Work happens in SocialStudio. Its buttons move the flow on."}</span>
      <button data-testid="sf-open-studio" onClick={open} className="ml-auto rounded-full bg-[#06060C] text-white text-xs px-3 py-1 hover:bg-[#1c1c24]">Open SocialStudio</button>
    </div>
  );
}

export function FlowBadge({ t }) {
  const cf = t.content_flow;
  if (!cf || t.status !== "Active Flow" || !BADGE_ICON[cf.stage]) return null;
  const n = Math.max(0, (cf.round || 1) - 1);
  const I = BADGE_ICON[cf.stage];
  const label = cf.stage === "brief" ? "Brief" : cf.stage === "rework" ? `Rework ${n}` : `${cf.stage === "draft" ? "Draft" : "Review"}${n ? ` · ${n} rework${n === 1 ? "" : "s"}` : ""}`;
  return (
    <span data-testid={`flow-badge-${t.id}`} className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-mono-data text-[9px] uppercase tracking-wide border ${cf.stage === "rework" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-[#EDE7FF] text-[#5b2fd6] border-[#d9ccff]"}`}>
      <I className="h-2.5 w-2.5" />{label}
    </span>
  );
}
